import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GCodeViewer } from 'react-gcode-viewer';
import { StlViewer } from 'react-stl-viewer';

const VIEWER_HEIGHT = { xs: 260, md: 340 };

const getEntryFilename = (entry = {}) => String(entry.filename || '').trim();

const getFilenameExtension = (filename = '') => {
  const normalized = String(filename || '').trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex) : '';
};

const parseAscText = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const header = {
    ncols: 0,
    nrows: 0,
    xllcorner: 0,
    yllcorner: 0,
    cellsize: 1,
    nodata: -9999,
  };
  let headerOffset = 0;
  let recognizedHeaderLines = 0;

  for (let index = 0; index < Math.min(lines.length, 10); index += 1) {
    const line = lines[index].trim().toLowerCase();
    if (!line) {
      continue;
    }
    const [key, rawValue] = line.split(/\s+/);
    const numericValue = Number(rawValue);
    if (key === 'ncols') {
      header.ncols = Number.parseInt(rawValue, 10) || 0;
    } else if (key === 'nrows') {
      header.nrows = Number.parseInt(rawValue, 10) || 0;
    } else if (key === 'xllcorner' || key === 'xllcenter') {
      header.xllcorner = Number.isFinite(numericValue) ? numericValue : 0;
    } else if (key === 'yllcorner' || key === 'yllcenter') {
      header.yllcorner = Number.isFinite(numericValue) ? numericValue : 0;
    } else if (key === 'cellsize') {
      header.cellsize = Number.isFinite(numericValue) && numericValue !== 0 ? numericValue : 1;
    } else if (key === 'nodata_value') {
      header.nodata = Number.isFinite(numericValue) ? numericValue : -9999;
    } else {
      headerOffset = index;
      break;
    }
    recognizedHeaderLines += 1;
    headerOffset = index + 1;
  }

  const positions = [];
  const hasRasterHeader = recognizedHeaderLines >= 4 && header.ncols > 0 && header.nrows > 0;
  if (hasRasterHeader) {
    let rowCount = 0;
    for (let index = headerOffset; index < lines.length; index += 1) {
      const line = lines[index].trim();
      if (!line) {
        continue;
      }
      line.split(/\s+/).forEach((valueText, colCount) => {
        const z = Number.parseFloat(valueText);
        if (Number.isFinite(z) && z !== header.nodata) {
          const x = header.xllcorner + colCount * header.cellsize;
          const y = header.yllcorner + (header.nrows - 1 - rowCount) * header.cellsize;
          positions.push(x, z, -y);
        }
      });
      rowCount += 1;
    }
  } else {
    lines.forEach((line) => {
      const values = line.trim().split(/\s+/).map((value) => Number.parseFloat(value));
      if (values.length >= 3 && values.slice(0, 3).every(Number.isFinite)) {
        positions.push(values[0], values[2], -values[1]);
      }
    });
  }

  if (!positions.length) {
    throw new Error('No numeric point data was found in the ASC file.');
  }

  return normalizePositions(new Float32Array(positions));
};

const normalizePositions = (positions) => {
  const count = positions.length / 3;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let index = 0; index < positions.length; index += 3) {
    for (let axis = 0; axis < 3; axis += 1) {
      const value = positions[index + axis];
      min[axis] = Math.min(min[axis], value);
      max[axis] = Math.max(max[axis], value);
    }
  }

  const center = [
    (min[0] + max[0]) / 2,
    (min[1] + max[1]) / 2,
    (min[2] + max[2]) / 2,
  ];
  const span = Math.max(max[0] - min[0], max[1] - min[1], max[2] - min[2], 1);
  const scale = 8 / span;
  const normalized = new Float32Array(positions.length);
  for (let index = 0; index < positions.length; index += 3) {
    normalized[index] = (positions[index] - center[0]) * scale;
    normalized[index + 1] = (positions[index + 1] - center[1]) * scale;
    normalized[index + 2] = (positions[index + 2] - center[2]) * scale;
  }
  return {
    positions: normalized,
    pointCount: count,
  };
};

const useAscParser = (fileUrl) => {
  const [state, setState] = useState({ loading: false, error: '', data: null });

  useEffect(() => {
    if (!fileUrl) {
      setState({ loading: false, error: '', data: null });
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true, error: '', data: null });
    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load ASC file (${response.status}).`);
        }
        return response.text();
      })
      .then((text) => {
        if (!cancelled) {
          setState({ loading: false, error: '', data: parseAscText(text) });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ loading: false, error: error.message || 'Unable to parse ASC file.', data: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  return state;
};

function AscPointCloud({ data }) {
  const pointsRef = useRef(null);

  useFrame(() => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += 0.0018;
    }
  });

  if (!data?.positions) {
    return null;
  }

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[data.positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.035} color="#00ffcc" sizeAttenuation />
    </points>
  );
}

function PointCloudOrbitControls() {
  const controlsRef = useRef(null);
  const { camera, gl } = useThree();

  useEffect(() => {
    const controls = new ThreeOrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controlsRef.current = controls;

    return () => {
      controls.dispose();
      controlsRef.current = null;
    };
  }, [camera, gl]);

  useFrame(() => {
    controlsRef.current?.update();
  });

  return null;
}

const ViewerFrame = ({ children }) => (
  <Box
    sx={{
      height: VIEWER_HEIGHT,
      minHeight: 240,
      width: '100%',
      borderRadius: 1.5,
      overflow: 'hidden',
      bgcolor: '#111827',
      position: 'relative',
    }}
  >
    {children}
  </Box>
);

const ViewerLoading = ({ label = 'Loading viewer...' }) => (
  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', bgcolor: '#111827', color: '#e5e7eb' }}>
    <Stack spacing={1} alignItems="center">
      <CircularProgress size={22} color="inherit" />
      <Typography variant="caption" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
    </Stack>
  </Box>
);

const ViewerError = ({ message }) => (
  <Alert severity="warning" sx={{ height: '100%', alignItems: 'center' }}>
    {message || 'Unable to render this file.'}
  </Alert>
);

const StlTimelineViewer = ({ url }) => {
  const [error, setError] = useState('');

  if (error) {
    return (
      <ViewerFrame>
        <ViewerError message={error} />
      </ViewerFrame>
    );
  }

  return (
    <ViewerFrame>
      <StlViewer
        style={{ width: '100%', height: '100%' }}
        orbitControls
        shadows
        showAxes
        url={url}
        modelProps={{ color: '#10b981' }}
        floorProps={{ gridWidth: 12, gridLength: 12 }}
        onError={(err) => setError(err?.message || 'Unable to render STL file.')}
      />
    </ViewerFrame>
  );
};

const GCodeTimelineViewer = ({ url }) => {
  const [error, setError] = useState('');

  if (error) {
    return (
      <ViewerFrame>
        <ViewerError message={error} />
      </ViewerFrame>
    );
  }

  return (
    <ViewerFrame>
      <GCodeViewer
        style={{ width: '100%', height: '100%' }}
        url={url}
        orbitControls
        showAxes
        visible={1}
        quality={0.92}
        layerColor="#38bdf8"
        topLayerColor="#f97316"
        floorProps={{ gridWidth: 220, gridLength: 220 }}
        cameraInitialPosition={{ latitude: 0.65, longitude: 0.75, distance: 240 }}
        onError={(err) => setError(err?.message || 'Unable to render GCode file.')}
      />
    </ViewerFrame>
  );
};

const AscTimelineViewer = ({ url }) => {
  const { loading, error, data } = useAscParser(url);

  if (loading) {
    return (
      <ViewerFrame>
        <ViewerLoading label="Loading point cloud..." />
      </ViewerFrame>
    );
  }
  if (error) {
    return (
      <ViewerFrame>
        <ViewerError message={error} />
      </ViewerFrame>
    );
  }

  return (
    <ViewerFrame>
      <Canvas camera={{ position: [8, 7, 11], fov: 55 }}>
        <color attach="background" args={['#111827']} />
        <ambientLight intensity={1.4} />
        <directionalLight position={[6, 8, 6]} intensity={1.1} />
        <gridHelper args={[12, 12, '#334155', '#1f2937']} position={[0, -3.8, 0]} />
        <AscPointCloud data={data} />
        <PointCloudOrbitControls />
      </Canvas>
    </ViewerFrame>
  );
};

const PlaceholderVisual = ({ title }) => (
  <ViewerFrame>
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        color: '#e5e7eb',
        bgcolor: '#111827',
        p: 2,
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 850 }}>
        {title || 'No native renderer configured for this file type.'}
      </Typography>
    </Box>
  </ViewerFrame>
);

const useThreadFileUrls = ({ selectedEntries, onCreateFileDownloadUrl }) => {
  const [state, setState] = useState({ loading: false, error: '', urls: {} });
  const requestKey = useMemo(
    () => selectedEntries.map((entry) => `${entry.name}:${getEntryFilename(entry)}`).join('|'),
    [selectedEntries],
  );
  const requestEntries = useMemo(
    () => selectedEntries
      .map((entry) => ({
        name: entry.name,
        filename: getEntryFilename(entry),
      }))
      .filter((entry) => entry.filename),
    [requestKey],
  );

  useEffect(() => {
    if (!requestEntries.length) {
      setState({ loading: false, error: '', urls: {} });
      return undefined;
    }
    if (!onCreateFileDownloadUrl) {
      setState({ loading: false, error: 'File preview links are not available in this view.', urls: {} });
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true, error: '', urls: {} });
    Promise.all(requestEntries.map(async (entry) => {
      const url = await onCreateFileDownloadUrl(entry.filename);
      return [entry.name, url];
    }))
      .then((pairs) => {
        if (!cancelled) {
          setState({ loading: false, error: '', urls: Object.fromEntries(pairs) });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ loading: false, error: error?.message || 'Unable to create file preview links.', urls: {} });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, onCreateFileDownloadUrl, requestEntries]);

  return state;
};

export default function ThreadFileRenderers({
  config,
  selectedEntries = [],
  onCreateFileDownloadUrl,
}) {
  const { loading, error, urls } = useThreadFileUrls({ selectedEntries, onCreateFileDownloadUrl });
  const visualizationType = config?.visualizationType;

  if (loading) {
    return (
      <ViewerFrame>
        <ViewerLoading label="Preparing file preview..." />
      </ViewerFrame>
    );
  }
  if (error) {
    return (
      <ViewerFrame>
        <ViewerError message={error} />
      </ViewerFrame>
    );
  }

  if (visualizationType === 'gcode_toolpath' && urls.filename) {
    return <GCodeTimelineViewer url={urls.filename} />;
  }
  if (
    visualizationType === 'mesh_3d' &&
    urls.filename &&
    getFilenameExtension(getEntryFilename(selectedEntries.find((entry) => entry.name === 'filename'))) === '.stl'
  ) {
    return <StlTimelineViewer url={urls.filename} />;
  }
  if (visualizationType === 'scan_point_cloud_mesh') {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1 }}>
        {urls.point_cloud_file ? <AscTimelineViewer url={urls.point_cloud_file} /> : <PlaceholderVisual title="Select an ASC point cloud file." />}
        {urls.mesh_file ? <StlTimelineViewer url={urls.mesh_file} /> : <PlaceholderVisual title="Select an STL mesh file." />}
      </Box>
    );
  }

  return <PlaceholderVisual title="Native rendering is available for STL, GCode, and ASC scan files." />;
}
